import {
  IntegrationTestCase,
  IntegrationTestCaseDetail,
  IntegrationTestSuite,
} from 'common/types/kraken-core/IntegrationTest'
import { createContext, useContext, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { getIntegrationTestCaseDetail } from 'services/kraken-core/integration_test/integration_test.service'
import {
  useGetIntegrationTestCaseDetail,
  useUpdateIntegrationTestCase,
} from './IntegrationTestCase/api'

export interface IntegrationTestContextState {
  suite?: IntegrationTestSuite
  testCase?: IntegrationTestCaseDetail
  isTestCaseDetailViewOpened: boolean

  // Actions
  setSuite: (suite: IntegrationTestSuite) => void
  createNewTestCase: () => void
  openTestCaseDetails: (id: string) => void
  toggleTestCaseDetailView: () => void
}

export const IntegrationTestContext = createContext<IntegrationTestContextState | undefined>({
  isTestCaseDetailViewOpened: false,
  setSuite: (suite: IntegrationTestSuite) => {},
  createNewTestCase: () => {},
  openTestCaseDetails: (id: string) => {},
  toggleTestCaseDetailView: () => {},
})

export interface IntegrationTestContextWrapperProps {
  children: any
}

export function useIntegrationTestContext() {
  const context = useContext(IntegrationTestContext)
  if (context === undefined)
    throw new Error(
      'useIntegrationTestContext must be used within a IntegrationTestContextProvider'
    )
  return context
}

export default function IntegrationTestContextProvider({
  children,
}: IntegrationTestContextWrapperProps) {
  const [suite, setSuite] = useState<IntegrationTestSuite | undefined>()
  const [testCase, setTestCase] = useState<IntegrationTestCaseDetail | undefined>()
  const [testCaseId, setTestCaseId] = useState<string>()
  const [isTestCaseDetailViewOpened, toggleTestCaseDetailView] = useState<boolean>(false)

  const {
    data: fetchedTestCase,
    refetch: fetchTestCaseDetail,
    isLoading: isLoadingTestCase,
    isSuccess: isSuccessTestCase,
    error: errorTestCase,
  } = useGetIntegrationTestCaseDetail(
    {
      id: testCaseId,
      suite_id: suite?.id,
    },
    {
      enabled: false,
      cacheTime: 0,
      staleTime: 0,
      useErrorBoundary: true,
      onError: (error: any) => {
        toast.error(`Error when fetching: ${error}`)
      },
    }
  )

  const handleOpenTestCaseDetails = (id: string) => {
    toggleTestCaseDetailView(true)
    setTestCaseId(id)
  }

  const handleNewTestCase = () => {
    setTestCaseId(undefined)
    setTestCase({
      suite: suite?.id,
      http_mocks: [],
    })
    toggleTestCaseDetailView(true)
  }

  const handleToggleTestCaseView = () => {
    if (isTestCaseDetailViewOpened) {
      setTestCaseId(undefined)
    }
    toggleTestCaseDetailView(c => !c)
  }

  useEffect(() => {
    if (testCaseId) {
      fetchTestCaseDetail().then(r => {
        if (r.data) {
          setTestCase(r.data)
        }
      })
    }
  }, [testCaseId])

  return (
    <IntegrationTestContext.Provider
      value={{
        // State
        suite,
        testCase,
        isTestCaseDetailViewOpened,

        // Actions
        setSuite,
        createNewTestCase: handleNewTestCase,
        openTestCaseDetails: handleOpenTestCaseDetails,
        toggleTestCaseDetailView: handleToggleTestCaseView,
      }}
    >
      {children}
    </IntegrationTestContext.Provider>
  )
}
